import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import AppHeader from "../../components/AppHeader";
import AppSidebar from "../../components/AppSidebar";
import PageHeader from "../../components/PageHeader";
import PlanService from "../../services/PlanService";
import GroupService from "../../services/GroupService";
import { toast, ToastContainer } from "react-toastify";
import GroupChecklist from "../../components/GroupChecklist";

const CreatePlan = () => {
    const { register, handleSubmit, reset, formState: { errors }, control } = useForm({
        defaultValues: {
            checklists: [],
            is_billing_cycle: false
        },
    });
    const [loading, setLoading] = useState(false);
    const [groupChecklist, setGroupChecklist] = useState([]);
    const [isBillingCycle, setIsBillingCycle] = useState(false);

    const fetchGroupsWithChecklists = async () => {
        try {
            const response = await GroupService.checklist();
            if (response.status === 200) {
                setGroupChecklist(response?.data?.data);
            } else {
                console.error("Error in fetching groups", response);
                toast.error(response?.data?.message);
            }
        } catch (error) {
            console.error("Error in fetching groups", error);
            toast.error(error?.message || "An error occurred");
        }
    }
    const onSubmit = async (data) => {
        setLoading(true);
        const selectedChecklistIds = [];
        data.checklists.forEach(group => {
            group.checklists.forEach(checklist => {
                if (checklist.isChecked) {
                    selectedChecklistIds.push(checklist.id);
                }
            });
        });
        data.checklists = selectedChecklistIds;
        try {
            const response = await PlanService.create({ data });
            if (response.status === 201) {
                setIsBillingCycle(false);
                toast.success(response?.data?.message);
                reset({
                    checklists: groupChecklist,
                    is_billing_cycle: false,
                    label: "",
                    name: "",
                    price: "",
                    level: "",
                    billing_cycle: "",
                    duration: "",
                    no_of_tickets: "",
                    description: "",
                });
            } else {
                console.error("Error in creating group", response);
                toast.error(response?.data?.message);
            }
        } catch (error) {
            console.error("Error in creating group", error);
            toast.error(error?.message || "An error occurred");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGroupsWithChecklists();
    }, [reset]);

    return (
        <>
            <AppSidebar />
            <div className="main-content">
                <AppHeader/>
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12">
                            <PageHeader
                                title="Create Plan"
                                button_label1="Back"
                                link_to1="/plans"
                                className1="btn btn-sm btn-primary"
                            />
                            <div className="row mt-5">
                                <div className="col-12 col-xl-12">
                                    <div className="card">
                                        <div className="card-body">
                                            <form onSubmit={handleSubmit(onSubmit)}>
                                                <div className="row">
                                                    {/* Label */}
                                                    <div className="col-12 col-md-6 mb-3">
                                                        <label className="form-label">Label</label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errors.label ? "is-invalid" : ""}`}
                                                            {...register("label", {
                                                                maxLength: {
                                                                    value: 100,
                                                                    message: "Plan label cannot exceed 100 characters",
                                                                },
                                                            })}
                                                            placeholder="Enter label"
                                                        />
                                                        {errors.label && (
                                                            <p className="text-danger">{errors.label.message}</p>
                                                        )}
                                                    </div>
                                                    {/* Name */}
                                                    <div className="col-12 col-md-6 mb-3">
                                                        <label className="form-label">Name</label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errors.name ? "is-invalid" : ""}`}
                                                            {...register("name", {
                                                                required: "Plan name is required",
                                                                maxLength: {
                                                                    value: 100,
                                                                    message: "Plan name cannot exceed 100 characters",
                                                                },
                                                            })}
                                                            placeholder="Enter Plan name"
                                                        />
                                                        {errors.name && (
                                                            <p className="text-danger">{errors.name.message}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-12 col-md-6 mb-3">
                                                        <div className="d-flex align-items-center pt-2">
                                                            <label className="form-label">Is Billing Cycle</label>
                                                            <div className="w-25 d-flex justify-content-end">
                                                                <div className="form-check form-switch me-n3">
                                                                    <input type="checkbox" className="form-check-input"  {...register("is_billing_cycle")} checked={isBillingCycle} onChange={(e) => setIsBillingCycle(e.target.checked)} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                {isBillingCycle && (
                                                    <div className="row">
                                                        {/* Price */}
                                                        <div className="col-12 col-md-6 mb-3">
                                                            <label className="form-label">Price</label>
                                                            <input
                                                                type="text"
                                                                className={`form-control ${errors.price ? "is-invalid" : ""}`}
                                                                {...register("price", {
                                                                    required: isBillingCycle ? "Price is required" : false,
                                                                    maxLength: {
                                                                        value: 20,
                                                                        message: "Plan price cannot exceed 20 characters",
                                                                    },
                                                                })}
                                                                min={0}
                                                                placeholder="Enter price"
                                                            />
                                                            {errors.price && <p className="text-danger">{errors.price.message}</p>}
                                                        </div>
                                                        {/* Duration */}
                                                        <div className="col-12 col-md-6 mb-3">
                                                            <label className="form-label">Duration</label>
                                                            <select
                                                                className={`form-select ${errors.duration ? "is-invalid" : ""}`}
                                                                {...register("duration", {
                                                                    required: isBillingCycle ? "Duration is required" : false,
                                                                })}
                                                            >
                                                                <option value="">Select Duration</option>
                                                                <option value="monthly">Monthly</option>
                                                                <option value="3 monthly">3 Monthly</option>
                                                                <option value="6 monthly">6 Monthly</option>
                                                                <option value="yearly">Yearly</option>
                                                            </select>
                                                            {errors.duration && <p className="text-danger">{errors.duration.message}</p>}
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="row">
                                                    <div className="col-12 col-md-6 mb-3">
                                                        <label className="form-label">No. of Tickets</label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${errors.no_of_tickets ? "is-invalid" : ""}`}
                                                            {...register("no_of_tickets", {
                                                                required: "No. of tickets are required",
                                                            })}
                                                            placeholder="Enter no. of tickets"
                                                        />
                                                        {errors.no_of_tickets && (
                                                            <p className="text-danger">{errors.no_of_tickets.message}</p>
                                                        )}
                                                    </div>
                                                    <div className="col-12 col-md-6 mb-3">
                                                        <label className="form-label">Level</label>
                                                        <select
                                                            className={`form-select ${errors.level ? "is-invalid" : ""}`}
                                                            defaultValue="0"
                                                            {...register("level")}
                                                        >
                                                            <option value="0">Select Level</option>
                                                            <option value="1">1</option>
                                                            <option value="2">2</option>
                                                            <option value="3">3</option>
                                                        </select>
                                                        {errors.level && (
                                                            <p className="text-danger">{errors.level?.message}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="row">
                                                    {/* Description */}
                                                    <div className="col-12 mb-3">
                                                        <label className="form-label">Description</label>
                                                        <textarea
                                                            className={`form-control ${errors.description ? "is-invalid" : ""}`}
                                                            {...register("description", {
                                                                maxLength: {
                                                                    value: 255,
                                                                    message: "Description cannot exceed 255 characters",
                                                                },
                                                            })}
                                                            rows="2"
                                                            placeholder="Enter description"
                                                        ></textarea>
                                                        {errors.description && (
                                                            <p className="text-danger">{errors.description.message}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="row mt-3">
                                                    {/* Group Checklists */}
                                                    <label className="form-label">Group Checklists</label>
                                                    <GroupChecklist control={control} data={groupChecklist} register={register} />
                                                    {errors.checklists && (
                                                        <p className="text-danger">{errors.checklists?.message}</p>
                                                    )}
                                                </div>
                                                <div className="text-end">
                                                <button type="submit" className="btn btn-primary" disabled={loading}>
                                                    {loading ? 'Saving...' : 'Save'}
                                                </button>
                                                {loading && (
                                                    <div className="mt-3">
                                                        <div className="spinner-border text-primary" role="status">
                                                            <span className="sr-only">Loading...</span>
                                                        </div>
                                                    </div>
                                                )}
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
            <ToastContainer />
        </>
    );
};

export default CreatePlan;
